import { useEffect, useState, useCallback } from "react";

import { Box, Button, Center, Flex, FormControl, FormErrorMessage, FormLabel, Input, Spinner } from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { AxiosError } from "axios";
import { SubmitHandler, useForm } from "react-hook-form";
import { NavLink, useLocation } from "react-router-dom";
import * as Yup from "yup";

import { useDevice, useNavigate, useTitle, useQueryParam } from "~/hooks";
import { useLanguage } from "~/hooks/useLanguage";
import useNamespace from "~/hooks/useNamespace";
import { sendEvent, EventType, FeatureType } from "~/services/amplitude";
import { Alert, Password } from "~/theme/components";
import { ArrowLeft } from "~/theme/icons";
import { yupTest } from "~/utils/passVerify";
import { addOnboardingSkipParam } from "~/utils/url";

import { useLoggedOut } from "../Layout";
import { ProductSpan } from "../Layout/components";
import Footer from "./Footer";

export interface SignInProps {
  email: string;
  password: string;
}

const verifySchema = Yup.object({
  email: Yup.string().required("signIn@validation-email-required").email("signIn@validation-email-invalid"),
}).required();

const schema = Yup.object({
  email: Yup.string().required("signIn@validation-email-required").email("signIn@validation-email-invalid"),
  password: Yup.string()
    .required("signIn@validation-password-required")
    .test(...yupTest("signIn")),
}).required();

function SignIn() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const { auth, ...product } = useLoggedOut();
  const { translate } = useNamespace("signIn");
  const { translate: translateGeneral } = useNamespace("forgotPassword");
  const { isMobileDevice } = useDevice();
  const { getCurrentLanguage } = useLanguage();
  const [loadingVerify, setLoadingVerify] = useState<boolean>(false);
  const params = useQueryParam("product");
  const emailParam = useQueryParam("email");
  const trailParam = useQueryParam("trail");
  const [verifyState, setVerifyState] = useState<"verify" | "signin">(emailParam?.email ? "signin" : "verify");

  const {
    formState: verifyFormState,
    register: verifyRegister,
    handleSubmit: verifyHandleSubmit,
    getValues,
    clearErrors,
    setValue: setVerifyValue,
  } = useForm<Pick<SignInProps, "email">>({
    resolver: yupResolver(verifySchema),
    reValidateMode: "onChange",
    criteriaMode: "all",
    mode: "all",
  });

  const { formState, register, handleSubmit, setValue } = useForm<SignInProps>({
    resolver: yupResolver(schema),
    reValidateMode: "onChange",
    criteriaMode: "all",
    mode: "all",
  });

  const onVerify: SubmitHandler<Pick<SignInProps, "email">> = useCallback(
    async ({ email }) => {
      setLoadingVerify(true);
      fetch(`${import.meta.env.VITE_API_URL}/auth/activate/verify/?email=${email}`).then((response) => {
        setLoadingVerify(false);
        if (response?.status === 200) {
          const emailEncodeURI = encodeURIComponent(email);

          if (params.product) {
            navigate(`/activate/?email=${emailEncodeURI}&product=${params.product}`);

            return;
          }

          if (trailParam.trail) {
            navigate(`/activate/?email=${emailEncodeURI}&trail=${trailParam.trail}`);

            return;
          }

          navigate(`/activate/?email=${emailEncodeURI}`);

          return;
        }
        setVerifyState("signin");
        setValue("email", getValues("email"));
      });
    },
    [params.product, trailParam.trail, navigate, setValue, getValues]
  );

  const onSubmit: SubmitHandler<SignInProps> = async ({ email, password }) => {
    let modifiedCallbackUrl = addOnboardingSkipParam(product.callback_url ?? "", product.name);

    if (trailParam.trail) {
      modifiedCallbackUrl = `?trail=${trailParam.trail}`;
    }
    auth.handleSignIn(email, password, modifiedCallbackUrl);
  };

  useTitle(`${translate("signIn@title")} | ${product.name}`);

  useEffect(() => {
    sendEvent({ event: EventType.LOGIN_STARTED, feature: FeatureType.LOGIN });
  }, []);

  useEffect(() => {
    if (emailParam?.email) {
      setValue("email", emailParam.email);
    }
  }, [emailParam?.email, setValue]);

  if (auth.loading) {
    return (
      <Center width="100%" height="100%">
        <Spinner boxSize="12" thickness="0.25rem" color="purple.300" margin="auto" />
      </Center>
    );
  }

  return (
    <main className="flex-1 flex flex-col gap-[30px] max-w-[437px]">
      <div className="bg-basic-white-50 p-[38px] rounded-3xl">
        {verifyState === "signin" ? (
          <Flex width="100%" flexDirection="column" alignItems="flex-start">
            <Button
              variant="terciary"
              data-type="terciary"
              leftIcon={<ArrowLeft />}
              onClick={() => {
                setVerifyState("verify");
                setValue("email", "");
                setVerifyValue("email", "");
                clearErrors("email");
              }}
            >
              {translateGeneral("forgotPassword@go-back-button")}
            </Button>
            <Box textStyle="h5" color="gray.900" mt="4">
              {/* {translate("signIn@signin-title")} */}
            </Box>
          </Flex>
        ) : (
          <Flex direction="column" align="center" justify="center">
            <Flex direction="column" justify="center" gap="2">
              <ProductSpan />
              <Box textStyle="h5" textAlign="center" color="gray.900">
                {/* {translate("signIn@signin-title")} */}
              </Box>
            </Flex>
          </Flex>
        )}
        {auth.error && (
          <Alert status="error">
            {(auth.error as AxiosError)?.response?.status === 404
              ? translate("signIn@error-auth-404")
              : (auth.error as AxiosError).message}
          </Alert>
        )}
        {verifyState === "verify" ? (
          <FormControl
            display="flex"
            flexDirection="column"
            gridRowGap={["4", "8"]}
            as="form"
            width="100%"
            isInvalid={verifyFormState.isDirty}
          >
            <Flex direction="column" width="inherit">
              <FormLabel htmlFor="email" textStyle="label.primary" color="black">
                {translate("signIn@form-email-label")}
              </FormLabel>
              <Input
                id="email"
                type="email"
                data-testid="email"
                isInvalid={!!verifyFormState.errors.email}
                required
                color="black"
                {...verifyRegister("email", { value: emailParam?.email || "" })}
                onChange={() => sendEvent({ event: EventType.EMAIL_ADDED, feature: FeatureType.LOGIN })}
              />
              {verifyFormState.errors.email && (
                <FormErrorMessage>{translate("signIn@validation-email-invalid")}</FormErrorMessage>
              )}
            </Flex>
            <Button
              type="submit"
              isLoading={loadingVerify}
              onClick={verifyHandleSubmit(onVerify)}
              data-testid="submit"
              data-type="primary"
              isFullWidth
            >
              {translate("signIn@form-signin-button")}
            </Button>
          </FormControl>
        ) : (
          <FormControl
            display="flex"
            flexDirection="column"
            gridRowGap={["4", "8"]}
            as="form"
            width="100%"
            isInvalid={formState.isDirty}
          >
            <Flex direction="column" width="100%" alignItems="flex-start" gridRowGap="2">
              <Flex direction="column" width="inherit">
                <FormLabel htmlFor="email" textStyle="label.primary" color="black">
                  {translate("signIn@form-email-label")}
                </FormLabel>
                <Input
                  id="email"
                  type="email"
                  data-testid="email"
                  isInvalid={!!formState.errors.email}
                  disabled
                  required
                  color="black"
                  {...register("email")}
                />
                {formState.errors.email && (
                  <FormErrorMessage>
                    {translate(formState.errors.email.message as "signIn@validation-email-invalid")}
                  </FormErrorMessage>
                )}
              </Flex>
              <Flex direction="column" width="inherit" mt="2">
                <FormLabel htmlFor="password" textStyle="label.primary" color="black">
                  {translate("signIn@form-password-label")}
                </FormLabel>
                <Password
                  id="password"
                  data-testid="password"
                  isInvalid={!!formState.errors.password}
                  required
                  {...register("password")}
                />
                {formState.errors.password && (
                  <FormErrorMessage>
                    {translate(formState.errors.password.message as "signIn@validation-password-required")}
                  </FormErrorMessage>
                )}
              </Flex>
              <Button as={NavLink} to={`forgot-password/${search}`} variant="terciary">
                {translate("signIn@form-forgot-password")}
              </Button>
            </Flex>
            <Button
              type="submit"
              isDisabled={!formState.isValid}
              isLoading={auth.loading}
              onClick={handleSubmit(onSubmit)}
              data-testid="submit"
              data-type="primary"
              isFullWidth
            >
              {translate("signIn@form-signin-button")}
            </Button>
          </FormControl>
        )}
      </div>

      <Footer
        isMobileDevice={isMobileDevice}
        productName={product.name}
        navigate={navigate}
        search={search}
        externalLink={getCurrentLanguage().waitListUrl ?? undefined}
        fioDomain={import.meta.env.VITE_FIO_DOMAIN as string}
      />
    </main>
  );
}
export default SignIn;
